<script>
import { mapGetters } from 'vuex'

import { KC } from '@src/filters/currency.filters.js'
import LikableMixin from '@src/mixins/likable.js'
import Translatable from '@src/mixins/translatable.js'

export default {
  name: 'AttractionsList',
  mixins: [
    LikableMixin,
    Translatable
  ],
  props: {
    attractionsToDisplay: {
      type: Number
    }
  },
  data() {
    return {
      initialAttractionsCount: 15,
      incrementAttraction: 30,
      expanded: false,
    }
  },
  computed: {
    ...mapGetters('attractions', [
      'filteredAttractions',
    ]),
    displayedAttractions() {
      return this.filteredAttractions.slice(0, this.attractionsToDisplay)
    },
    btnLabel() {
      return this.filteredAttractions.length > this.attractionsToDisplay ? 'SHOW_MORE' : 'SHOW_LESS'
    },
  },
  methods: {
    toggleAttractions() {
      if (this.attractionsToDisplay < this.filteredAttractions.length) {
        this.moreAttractions()
      } else {
        this.scrollToElement({behavior: 'smooth'})
        setTimeout(() => this.lessAttractions(), 1000)
      }
    },
    moreAttractions() {
      const count = this.attractionsToDisplay + this.incrementAttraction
      this.$store.commit('attractions/SET_ATTRACTIONS_TO_DISPLAY', count)
    },
    lessAttractions() {
      this.$store.commit('attractions/SET_ATTRACTIONS_TO_DISPLAY', this.initialAttractionsCount)
    },
    scrollToElement(options) {
      const el = document.querySelector('#all-attractions')

      if (el) {
        el.scrollIntoView(options);
      }
    },
    getImage(attraction) {
      return `${process.env.VUE_APP_STATIC_URL}${attraction.images[0]}`
    },
    redirectToAttractionPage(slug) {
      this.$router.push({
        name: 'attraction',
        params: {
          slug: slug
        }
      })
    },
    price(attraction) {
      if (attraction.priceAdult && attraction.priceChild) {
        return `${KC(attraction.priceAdult)} / ${KC(attraction.priceChild)}`
      } else {
        return '-'
      }
    },
    benefitLabel(attraction) {
      return attraction.typology === 'pc_free' || !attraction.typology ?
      this.getTranslatedContent('ATTRACTIONS_label_included') : attraction.benefit
    }
  }
}
</script>

<template>
  <div class="list-container">
    <table>
      <thead>
        <tr>
          <th class="attractions-summary" colspan="2"></th>
          <th class="price-col price-title">
            {{ getTranslatedContent('ATTRACTIONS_normal_entry_price') }}
          </th>
          <th class="benefit-col">
            {{ getTranslatedContent('ATTRACTIONS_coolpass_benefit') }}
          </th>
          <th class="like-col"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="attraction in displayedAttractions"
          :key="attraction._id"
          @click="redirectToAttractionPage(attraction.slug)"
        >
          <td class="attraction-image-col">
            <img :src="getImage(attraction)" :alt="`${attraction.content.en.title}`" class="attraction-image">
          </td>
          <td class="attraction-row">
            {{ getTranslatedContent('title', attraction.content) }}
          </td>
          <td class="price">
            {{ price(attraction) }}
          </td>
          <td class="benefit-value">
            {{ benefitLabel(attraction) }}
          </td>
          <td class="like-col">
            <div
              class="like-button"
              :class="isLiked(attraction._id) ? 'liked' : ''"
              @click.stop="like(attraction._id)"
            >
              <i></i>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="mobile-list">
      <div
        v-for="attraction in displayedAttractions"
        :key="attraction._id"
        class="item"
        @click="redirectToAttractionPage(attraction.slug)"
      >
        <img :src="getImage(attraction)" :alt="`${attraction.content.en.title}`" class="attraction-image">
        <div class="item-content">
          <div class="row">
            <span class="title">{{ getTranslatedContent('title', attraction.content) }}</span>
            <span class="benefit">{{ attraction.benefit }}</span>
          </div>
          <div class="row">
            <span class="price">{{ price(attraction) }}</span>
            <div
              class="like-button"
              :class="isLiked(attraction._id) ? 'liked' : ''"
              @click.stop="like(attraction._id)"
            >
              <i></i>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="more-btn container">
      <button
        v-if="filteredAttractions.length > initialAttractionsCount"
        class="show-more-btn"
        @click.prevent="toggleAttractions"
      >
        {{ getTranslatedContent(btnLabel) }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 5px;
  background: $light-gray;
  user-select: none;
  cursor: pointer;

  & th,
  & td {
    vertical-align: middle;
  }
}

thead {
  background: $light-gray;

  & tr {
    width: 100%;
    height: 50px;
  }

  & th {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    text-align: right;
    color: $dark-blue;
  }
}

tbody {

  & tr {
    background: white;
    height: 70px;
    border-color: black;
    text-align: right;


    & td {

      &.attraction-row {
        width: 337px;

        text-align: left;
        vertical-align: middle;
        padding-left: 49px;

        font-family: Open Sans Bold, sans-serif;
        font-weight: bold;
        font-style: normal;
        font-size: 14px;
        line-height: 18px;
        text-transform: uppercase;
        color: #252527;
      }
    }
  }
}

.attractions-summary {
  width: 453px;
}

.price-col {
  width: 150px;
  text-align: right;
}

.price-title {
  width: 112px;
  height: 32px;
}

.like-col {
  width: 70px;
}

.benefit-col {
  text-align: right;
}

.attraction-image-col {
  width: 111px;
  vertical-align: top;
  padding-left: 5px;
  padding-top: 3px;
  padding-bottom: 3px;
  text-align: left;

  & img {
    border-radius: 5px;
    width: 111px;
    height: 66px;
    background-size: cover;
  }
}

.benefit-value,
.price {
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 18px;
  text-align: right;
  color: #252527;
  min-width: 165px;
}

.like-button {
  width: 24px;
  height: 24px;
  margin-left: auto;
  margin-right: auto;
  cursor: pointer;

  & i {
    display: block;
    height: 100%;
    background: url("../../assets/icons/heart-icon-dark.svg") center no-repeat;

    &:hover {
      background: url("../../assets/icons/heart-icon-dark-active.svg") center no-repeat;
      opacity: 0.8;
    }
  }

  &.liked i {
    display: block;
    height: 100%;
    background: url("../../assets/icons/heart-icon-dark-active.svg") center no-repeat;
  }
}

.show-more-btn {
  width: 270px;
  height: 48px;
  margin-top: 15px;

  border: 1px solid $dark-gray;
  box-sizing: border-box;
  border-radius: 5px;
  background-color: white;


  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 15px;
  line-height: 19px;
  text-align: center;
  text-transform: uppercase;
  color: rgba(37, 44, 62, 0.6);

  &:hover {
    border: 1px solid rgba(37, 44, 62, 0.6);
  }
}

.mobile-list {
  display: none;
}

.more-btn {
  display: flex;
  justify-content: flex-end;

  &.container {
    min-width: auto;
    width: auto;
  }
}

@media only screen and (max-width: $container-width-xs) {
  table {
    display: none;
  }

  .list-container {
    background: $light-gray;
  }

  .show-more-btn {
    width: 100%;
    margin-top: 10px;

    &:hover {
      border: 1px solid $dark-gray;
    }

    &:active {
      border: 1px solid rgba(37, 44, 62, 0.6);
    }
  }

  .mobile-list {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    row-gap: 4px;

    & .item {
      width: 100%;
      background: white;
      height: 92px;
      display: flex;
      flex-direction: row;
      align-items: center;

      & img {
        width: 111px;
        min-width: 111px;
        height: 88px;
        border-radius: 5px;
        margin-left: 2px;
      }

      & .item-content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 8px 11px 8px 8px;
        height: 100%;
        width: 100%;
        box-sizing: border-box;

        & .row {
          display: flex;
          flex-direction: row;

          & .title {
            max-width: 125px;
            height: 32px;

            font-family: Open Sans Semibold, sans-serif;
            font-style: normal;
            font-weight: 600;
            font-size: 12px;
            line-height: 16px;
            text-transform: uppercase;
            color: #252527;
          }

          & .benefit {
            font-family: Open Sans, sans-serif;
            font-style: normal;
            font-weight: normal;
            font-size: 12px;
            line-height: 16px;
            text-align: right;
            text-transform: uppercase;
            color: #252527;

            margin-left: auto;
          }

          & .price {
            font-family: Open Sans, sans-serif;
            font-style: normal;
            font-weight: normal;
            font-size: 12px;
            line-height: 16px;
            text-align: right;
            text-transform: uppercase;
            color: #252527;
            min-width: auto;
            margin-top: auto;
          }

          & .like-button {
            margin-left: auto;
            margin-right: 0;

            & i {
              &:hover {
                background: url("../../assets/icons/heart-icon-dark.svg") center no-repeat;
                opacity: 1;
              }
            }

            &.liked i {
              &:hover {
                background: url("../../assets/icons/heart-icon-dark-active.svg") center no-repeat;
              }
            }
          }
        }
      }
    }
  }
}

</style>
